import { useEffect, useState } from "react";
import { useAppConfig } from "../../../AppConfigProvider";
import { usePostHog } from "posthog-js/react";
import { useNavigation } from "@react-navigation/native";
import { useAppState, actions } from "../../../contexts/AppStateContext";
import { useLoginMutation, MeDocument, useLoginSuperAdminMutation } from "../../../graphql/generated/graphql";
import { useAuth } from "../../../hooks/useAuth";
import LoginForm from "../../../components/auth/LoginForm/LoginForm";
import { Platform } from "react-native";
import { announceForAccessibility } from "../../../utils/accessibility";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../../navigation/AppNavigator.web";
import Sentry from "../../../utils/sentry";
import { decodeToken } from "../../../contexts/AuthContext";
import returnToUtils from "../../../utils/returnTo.utils";
import AsyncStorage from "@react-native-async-storage/async-storage";

function LoginFormContainer({ isSuperAdminRoute }: { isSuperAdminRoute: boolean }) {
  const posthog = usePostHog();
  const { login, isUpdating } = useAuth();
  const appConfig = useAppConfig();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { dispatch } = useAppState();
  const [loginMutation] = useLoginMutation({
    refetchQueries: [{ query: MeDocument }],
  });
  const [loginSuperAdminMutation] = useLoginSuperAdminMutation({
    refetchQueries: [{ query: MeDocument }],
  });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordHidden, setPasswordHidden] = useState(true);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const logo = appConfig.logo;

  useEffect(() => {
    posthog?.capture("Login Page Visited");
  }, []);

  useEffect(() => {
    if (Platform.OS === "ios" && errorMessage) {
      announceForAccessibility({ message: "Error alert: " + errorMessage });
    }

    if (Platform.OS === "ios" && !isEmailValid) {
      announceForAccessibility({ message: "Please enter valid email" });
    }
  }, [errorMessage, isEmailValid]);

  const handleEmailChangeText = (text) => {
    setEmail(text);
  };

  const handlePasswordChangeText = (text) => {
    setPassword(text);
  };

  const handlePasswordToggle = () => {
    setPasswordHidden((prev) => !prev);
  };

  const handleSubmit = async () => {
    setErrorMessage("");
    if (!email || !email.trim()) {
      setErrorMessage("Email is required");
      return;
    }

    if (!password || !password.trim()) {
      setErrorMessage("Password is required");
      return;
    }

    if (!isEmailValid) {
      setErrorMessage("Invalid email");
      return;
    }
    const cleanedEmail = email.trim().toLowerCase();

    try {
      let result = null;
      let accessToken = null;
      let refreshToken = null;
      let user = null;
      let success = false;

      if (isSuperAdminRoute) {
        result = await loginSuperAdminMutation({
          variables: { email: cleanedEmail, password },
        });

        if (result.data) {
          accessToken = result.data.loginSuperAdmin.accessToken;
          refreshToken = result.data.loginSuperAdmin.refreshToken;
          user = result.data.loginSuperAdmin.user;
          success = true;
        }
      } else {
        result = await loginMutation({
          variables: { email: cleanedEmail, password },
        });

        if (result.data) {
          accessToken = result.data.login.accessToken;
          refreshToken = result.data.login.refreshToken;
          user = result.data.login.user;
          success = true;
        }
      }

      if (success) {
        await login(accessToken, refreshToken);
        const returnToUrl = await returnToUtils.getAndClearReturnToUrl();
        setErrorMessage("");
        dispatch({
          type: actions.SET_META,
          payload: { user, email: cleanedEmail },
        });
        const decoded = await decodeToken(accessToken);

        if (decoded.isEmailVerified && decoded.otpVerifiedForSession) {
          if (returnToUrl) {
            window.location.href = returnToUrl;
          } else {
            window.location.href = "/";
          }
        } else {
          navigation.navigate("Verify Email");
        }
      }
    } catch (error) {
      if (error.message === "User is not a super admin.") {
        navigation.navigate("Login");
      }
      Sentry.captureException(error);
      setErrorMessage(error.message);
    }
  };

  const handleResetPasswordPress = () => {
    navigation.navigate("Reset Password");
  };

  const handleSignupPress = () => {
    navigation.navigate("Signup");
  };

  const validateEmail = (rawEmail: string) => {
    const trimmed = rawEmail.trim().toLowerCase();
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsEmailValid(pattern.test(trimmed));
  };

  const handleSAMLSSOPress = async () => {
    const redirect = await AsyncStorage.getItem("axio_return_to");
    const baseUrl = "/api/sso/login";

    if (redirect && returnToUtils.isValidInternalUrl(redirect)) {
      const encodedRedirect = encodeURIComponent(redirect);
      window.location.href = `${baseUrl}?redirectUrl=${encodedRedirect}`;
    } else {
      window.location.href = baseUrl;
    }
  };

  return (
    <LoginForm
      email={email}
      password={password}
      isValidEmail={isEmailValid}
      errorMessage={errorMessage}
      logo={logo}
      onEmailChangeText={handleEmailChangeText}
      onPasswordChangeText={handlePasswordChangeText}
      passwordHidden={passwordHidden}
      onPasswordToggle={handlePasswordToggle}
      onEmailBlur={() => validateEmail(email)}
      onSubmit={handleSubmit}
      onResetPasswordPress={handleResetPasswordPress}
      onSignupPress={handleSignupPress}
      isLoading={isUpdating}
      hasSAMLSSO={appConfig.hasSAMLSSO}
      onSAMLSSOPress={handleSAMLSSOPress}
      requireSSO={!isSuperAdminRoute && appConfig.requireSSO}
    />
  );
}

export default LoginFormContainer;
